/**
 * Virtual Try-On — shared asset (Shopify assets/tryon.js)
 *
 * Loaded once via {{ 'tryon.js' | asset_url | script_tag }} and shared
 * across every section that needs virtual try-on (the standalone promo/
 * mobile sections and the product-detail-page modal). Each section's own
 * Liquid template stays responsible for its own markup/CSS and simply
 * calls window.VirtualTryOn.init(config) with its OWN element IDs — this
 * file never assumes or imposes any particular class names/markup, so it
 * never influences the existing UI. Everything here is purely behavior;
 * every feature (remove, download, lightbox) is opt-in via config and is
 * a no-op if the corresponding element ID isn't supplied.
 *
 * API reference: api.nairaa.shop/api/v1/tryon/ (see project reference
 * script for the original contract this was modeled on).
 *   POST  /api/v1/tryon/            -> { request_id }
 *   GET   /api/v1/tryon/{request_id} -> { status: 'completed'|'failed'|..., result_url }
 */
(function (window) {
  "use strict";

  var API_BASE = "https://api.nairaa.shop/api/v1/tryon/";
  var POLL_INTERVAL_MS = 3000;
  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var MAX_POLLS = 40; // ~2 minutes at 3s/poll — bounded, unlike unbounded reference polling

  /**
   * @param {Object} config
   * Required:
   * @param {string} config.fileInputId       - hidden <input type="file">
   * @param {string} config.runButtonId       - "Try On" button that submits the request
   * @param {string} config.beforeSlotId      - container to render the uploaded photo into
   * @param {string} config.productId         - Shopify product id
   *
   * Optional — purely additive, no-op if the ID isn't in your markup:
   * @param {string} [config.uploadButtonId]  - button that opens the file picker
   * @param {string} [config.afterSlotId]     - container to render the try-on result into
   * @param {string} [config.statusId]        - element for status/progress text
   * @param {string} [config.removeButtonId]  - clears the uploaded photo and resets state
   * @param {string} [config.downloadButtonId]- downloads the try-on result image
   * @param {string} [config.loadingClassTargetId] - element to receive a "loading" class
   *                                            while a request is in flight (e.g. your
   *                                            existing upload box, for a shimmer/dim state
   *                                            you already style via your own CSS)
   * @param {string} [config.overlayId]       - full-screen lightbox container (must already
   *                                            exist in your markup with its own styling);
   *                                            clicking the before/after image opens it
   * @param {string} [config.overlayImageId]  - <img> inside the overlay that receives the src
   * @param {string} [config.overlayCloseId]  - close button inside the overlay
   * @param {string} [config.garmentUrl]      - static fallback garment image URL
   * @param {Function} [config.getGarmentUrl] - optional fn returning the garment URL at
   *                                            submit time (e.g. read the shopper's
   *                                            currently active gallery image instead
   *                                            of always using the default product image)
   * @param {Function} [config.onSuccess]     - optional fn(resultUrl) called on completion,
   *                                            e.g. to also swap a PDP's main gallery image
   * @param {boolean}  [config.autoRun]      - start the try-on as soon as a photo is picked
   *                                            (no separate "Try On" click). runButtonId is
   *                                            then optional.
   * @param {number}   [config.maxFileBytes]  - reject larger uploads (default 10MB)
   * @param {string}   [config.downloadProxyUrl] - your own proxy (e.g. a Cloudflare Worker) used to
   *                                            fetch the result image when its host blocks CORS
   * @param {Function} [config.prepareFile]  - fn(file) -> Promise<File|null>; runs before the
   *                                            photo is accepted (crop/resize). null cancels.
   * @param {Function} [config.onPhotoSelected] - fn(dataUrl) when a valid photo is chosen
   * @param {Function} [config.onStatus]      - fn(text, kind) kind: loading|done|error|idle
   * @param {Function} [config.onReset]       - optional fn() called when the remove button
   *                                            clears the current photo/result
   * @returns {{ destroy: Function }} handle to tear down listeners/timers
   */
  function init(config) {
    var uploadBtn = config.uploadButtonId ? document.getElementById(config.uploadButtonId) : null;
    var fileInput = document.getElementById(config.fileInputId);
    var runBtn = document.getElementById(config.runButtonId);
    var beforeSlot = document.getElementById(config.beforeSlotId);
    var afterSlot = config.afterSlotId ? document.getElementById(config.afterSlotId) : null;
    var statusEl = config.statusId ? document.getElementById(config.statusId) : null;
    var removeBtn = config.removeButtonId ? document.getElementById(config.removeButtonId) : null;
    var downloadBtn = config.downloadButtonId ? document.getElementById(config.downloadButtonId) : null;
    var loadingTarget = config.loadingClassTargetId ? document.getElementById(config.loadingClassTargetId) : null;
    var overlay = config.overlayId ? document.getElementById(config.overlayId) : null;
    var overlayImg = config.overlayImageId ? document.getElementById(config.overlayImageId) : null;
    var overlayClose = config.overlayCloseId ? document.getElementById(config.overlayCloseId) : null;

    if (!fileInput || !beforeSlot || (!runBtn && !config.autoRun)) {
      console.error("[VirtualTryOn] required elements missing", config);
      return { destroy: function () {} };
    }

    // Move the overlay to <body> so it escapes any ancestor's Shopify
    // section stacking context (a real bug hit in the original
    // implementation this is modeled on) — only touches an element you
    // already own and already styled yourself.
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }

    var uploadedFile = null;
    var resultUrl = null;
    var pollTimer = null;
    var runToken = 0; // bumped on every run/reset so a cancelled request can never update the UI later

    function setStatus(text, kind) {
      if (statusEl) statusEl.textContent = text || "";
      if (typeof config.onStatus === "function") config.onStatus(text || "", kind || "idle");
    }

    function setRunDisabled(disabled) {
      if (runBtn) runBtn.disabled = disabled;
    }

    function setLoading(isLoading) {
      if (loadingTarget) loadingTarget.classList.toggle("loading", !!isLoading);
    }

    function setBeforeImage(src) {
      beforeSlot.innerHTML = '<img src="' + src + '" alt="Your photo">';
      bindLightbox(beforeSlot.querySelector("img"));
    }

    function setAfterImage(src) {
      if (!afterSlot) return;
      afterSlot.innerHTML = '<img src="' + src + '" alt="Try-on result">';
      bindLightbox(afterSlot.querySelector("img"));
    }

    function bindLightbox(imgEl) {
      if (!imgEl || !overlay || !overlayImg) return;
      imgEl.style.cursor = "pointer";
      imgEl.addEventListener("click", function () {
        overlayImg.src = imgEl.src;
        overlay.classList.remove("hidden");
        document.body.style.overflow = "hidden";
      });
    }

    function closeOverlay() {
      if (!overlay) return;
      overlay.classList.add("hidden");
      document.body.style.overflow = "";
      if (overlayImg) overlayImg.src = "";
    }

    function stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    function normalizeUrl(url) {
      if (!url) return url;
      // Protocol-relative URLs (e.g. "//cdn.shopify.com/...") are valid in
      // <img src> / CSS but get sent to the API as a literal string without
      // a scheme, which the API rejects. Match the reference implementation's
      // fix and force https: onto them before submitting.
      if (url.indexOf("//") === 0) return "https:" + url;
      return url;
    }

    function resolveGarmentUrl() {
      if (typeof config.getGarmentUrl === "function") {
        var dynamic = config.getGarmentUrl();
        if (dynamic) return normalizeUrl(dynamic);
      }
      return normalizeUrl(config.garmentUrl) || null;
    }

    function onFileChange() {
      var file = fileInput.files && fileInput.files[0];
      if (!file) return;
      if (file.type && file.type.indexOf("image/") !== 0) {
        fileInput.value = "";
        setStatus("Please choose an image file (JPG, PNG, WebP, AVIF...).", "error");
        return;
      }
      var limit = config.maxFileBytes || MAX_FILE_BYTES;
      if (file.size > limit) {
        fileInput.value = "";
        setStatus("That photo is too large. Please use one under " + Math.round(limit / 1048576) + "MB.", "error");
        return;
      }
      if (typeof config.prepareFile === "function" && !file.__prepared) {
        // Let the host edit the photo first (e.g. a crop dialog). Resolve with
        // a File to continue, or null/reject to cancel the upload.
        Promise.resolve(config.prepareFile(file)).then(function (prepared) {
          if (!prepared) { fileInput.value = ""; return; }
          prepared.__prepared = true;
          acceptFile(prepared);
        }).catch(function () {
          fileInput.value = "";
          setStatus("We couldn't open that image. Please try a JPG, PNG, WebP or AVIF photo.", "error");
        });
        return;
      }
      acceptFile(file);
    }

    function acceptFile(file) {
      uploadedFile = file;
      resultUrl = null;

      var reader = new FileReader();
      reader.onload = function (e) {
        setBeforeImage(e.target.result);
        setRunDisabled(false);
        if (typeof config.onPhotoSelected === "function") config.onPhotoSelected(e.target.result);
        if (config.autoRun) runTryOn(file);
      };
      reader.readAsDataURL(file);

      if (removeBtn) removeBtn.classList.remove("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");
    }

    function onRunClick() {
      if (!uploadedFile || (runBtn && runBtn.disabled)) return;
      runTryOn(uploadedFile);
    }

    function onRemoveClick(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      resetState();
    }

    // Save a Blob as a file without leaving the page.
    function saveBlob(blob) {
      var ext = ({ "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/avif": "avif" })[blob.type] || "png";
      var blobUrl = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = blobUrl;
      a.download = "virtual-tryon-" + Date.now() + "." + ext;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(blobUrl); }, 1000);
    }

    // Method 2: draw the image on a canvas and export it (needs the image host to send CORS headers).
    function downloadViaCanvas(url) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = function () {
          try {
            var c = document.createElement("canvas");
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext("2d").drawImage(img, 0, 0);
            c.toBlob(function (blob) {
              if (!blob) { reject(new Error("canvas export failed")); return; }
              saveBlob(blob);
              resolve();
            }, "image/png");
          } catch (e) { reject(e); }
        };
        img.onerror = function () { reject(new Error("image could not be loaded with CORS")); };
        img.src = url;
      });
    }

    function onDownloadClick() {
      if (!resultUrl) return;
      var url = resultUrl;

      // Method 1: fetch the file and save the blob. cache:"reload" matters: the browser has
      // already cached this image from the plain <img> display (without CORS headers) and
      // would otherwise reuse that entry and fail the CORS check.
      // Method 0 (optional): your own proxy, set in the theme editor. Needed when the
      // image host does not allow this site via CORS.
      var firstUrl = config.downloadProxyUrl
        ? config.downloadProxyUrl + (config.downloadProxyUrl.indexOf("?") > -1 ? "&" : "?") + "url=" + encodeURIComponent(url)
        : url;

      fetch(firstUrl, { mode: "cors", cache: "reload", credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.blob();
        })
        .then(saveBlob)
        .catch(function (err1) {
          return downloadViaCanvas(url).catch(function (err2) {
            // Last resort: the image host sends no CORS headers, so the browser will not let
            // a page save it directly. Open it so the shopper can still save it manually.
            console.warn("[VirtualTryOn] Direct download blocked. The image host (" + url.split("/")[2] +
              ") must allow this site via CORS (Access-Control-Allow-Origin), or serve the file with " +
              "Content-Disposition: attachment.", { fetchError: err1 && err1.message, canvasError: err2 && err2.message });
            window.open(url, "_blank");
          });
        });
    }

    function resetState() {
      runToken++;
      stopPolling();
      uploadedFile = null;
      resultUrl = null;
      fileInput.value = "";
      beforeSlot.innerHTML = "";
      if (afterSlot) afterSlot.innerHTML = "";
      setStatus("");
      setLoading(false);
      setRunDisabled(true);
      if (removeBtn) removeBtn.classList.add("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");
      if (typeof config.onReset === "function") config.onReset();
    }

    function runTryOn(file) {
      var garmentUrl = resolveGarmentUrl();

      if (!garmentUrl || !config.productId) {
        console.warn("[VirtualTryOn][DEBUG] blocked before request — missing garmentUrl or productId", {
          garmentUrl: garmentUrl,
          productId: config.productId
        });
        setStatus("Try-on is unavailable for this product.", "error");
        setRunDisabled(false);
        return;
      }

      setStatus("Processing…", "loading");
      setLoading(true);
      setRunDisabled(true);
      if (removeBtn) removeBtn.classList.add("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");

      var myRun = ++runToken;
      var formData = new FormData();
      formData.append("person_image", file);
      formData.append("garment_url", garmentUrl);
      formData.append("product_id", config.productId);
      formData.append("use_full_look", "true");

      fetch(API_BASE, { method: "POST", body: formData })
        .then(function (res) {
          if (!res.ok) {
            return res.text().then(function (bodyText) {
              console.error("[VirtualTryOn][DEBUG] submit failed body:", bodyText);
              throw new Error("Try-on request failed: " + res.status + " " + res.statusText);
            });
          }
          return res.json();
        })
        .then(function (data) {
          if (myRun !== runToken) return; // cancelled while the request was in flight
          if (!data.request_id) throw new Error("No request_id returned");
          pollResult(data.request_id, myRun);
        })
        .catch(function (err) {
          if (myRun !== runToken) return;
          console.error("[VirtualTryOn] submit error:", err);
          fail("Try-on failed: " + err.message);
        });
    }

    function pollResult(requestId, myRun) {
      var attempts = 0;
      stopPolling();

      pollTimer = setInterval(function () {
        attempts++;
        if (attempts > MAX_POLLS) {
          console.warn("[VirtualTryOn][DEBUG] poll timed out after", attempts, "attempts");
          stopPolling();
          fail("Try-on is taking longer than expected. Please try again.");
          return;
        }

        fetch(API_BASE + requestId)
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            if (myRun !== runToken) { stopPolling(); return; }
            if (data.status === "completed") {
              stopPolling();
              succeed(data.result_url || data.resultUrl);
            } else if (data.status === "failed") {
              console.error("[VirtualTryOn][DEBUG] API reported failed status. Full payload:", data);
              stopPolling();
              fail("Try-on failed" + (data.error || data.message ? (": " + (data.error || data.message)) : "."));
            }
          })
          .catch(function (err) {
            if (myRun !== runToken) return;
            console.error("[VirtualTryOn] poll error:", err);
            stopPolling();
            fail("Try-on failed while checking status: " + err.message);
          });
      }, POLL_INTERVAL_MS);
    }

    function succeed(url) {
      if (!url) {
        fail();
        return;
      }
      resultUrl = url;
      setAfterImage(url);
      setStatus("Done! Here's your try-on result.", "done");
      setLoading(false);
      setRunDisabled(false);
      if (removeBtn) removeBtn.classList.remove("hidden");
      if (downloadBtn) downloadBtn.classList.remove("hidden");
      if (typeof config.onSuccess === "function") config.onSuccess(url);
    }

    function fail(message) {
      setStatus(message || "Try-on failed. Please try again.", "error");
      setLoading(false);
      setRunDisabled(false);
      if (removeBtn) removeBtn.classList.remove("hidden");
    }

    if (uploadBtn) uploadBtn.addEventListener("click", function () { fileInput.click(); });
    fileInput.addEventListener("change", onFileChange);
    if (runBtn) runBtn.addEventListener("click", onRunClick);
    if (removeBtn) removeBtn.addEventListener("click", onRemoveClick);
    if (downloadBtn) downloadBtn.addEventListener("click", onDownloadClick);
    if (overlayClose) overlayClose.addEventListener("click", closeOverlay);
    if (overlay) {
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) closeOverlay();
      });
    }
    var escHandler = function (e) {
      if (e.key === "Escape") closeOverlay();
    };
    document.addEventListener("keydown", escHandler);

    return {
      // Stops any in-flight polling without tearing down event listeners —
      // use this when a modal/section is closed mid-request but may be
      // reopened later (e.g. a PDP try-on modal).
      stopPolling: stopPolling,
      destroy: function () {
        stopPolling();
        fileInput.removeEventListener("change", onFileChange);
        if (runBtn) runBtn.removeEventListener("click", onRunClick);
        if (removeBtn) removeBtn.removeEventListener("click", onRemoveClick);
        if (downloadBtn) downloadBtn.removeEventListener("click", onDownloadClick);
        if (overlayClose) overlayClose.removeEventListener("click", closeOverlay);
        document.removeEventListener("keydown", escHandler);
      }
    };
  }

  window.VirtualTryOn = { init: init };
})(window);
